class AddonSwitch extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handlePreChange = this.handlePreChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this._busy = false;
    this._currentVariantId = null;
  }

  get allInputs() {
    return this.querySelectorAll("input");
  }

  get allInputIds() {
    return [...this.allInputs]
      .map((input) => String(input.value))
      .filter((v) => Boolean(v) && v !== "none");
  }

  get isCartPage() {
    return window.location.pathname === "/cart";
  }

  getCart() {
    if (typeof upcartGetCart === "function") return upcartGetCart();
    console.warn("[addon-switch] upcartGetCart not available");
    return { items: [] };
  }

  showLoader() {
    if (document.querySelector(".addon-switch-loader")) return;
    const loader = document.createElement("div");
    loader.className = "addon-switch-loader";
    loader.innerHTML = `
      <style>
        @keyframes addon-spin { to { transform: rotate(360deg); } }
        @keyframes addon-fadein { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .addon-switch-loader {
          position: fixed; bottom: 20px; right: 20px; z-index: 99999;
          display: flex; align-items: center; gap: 8px;
          background: #000; color: #fff;
          font-family: sans-serif; font-size: 13px; font-weight: 500;
          padding: 10px 14px; border-radius: 999px;
          pointer-events: none;
          animation: addon-fadein 0.15s ease;
        }
        .addon-switch-loader__spinner {
          width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
          border-radius: 50%;
          animation: addon-spin 0.7s linear infinite;
          flex-shrink: 0;
        }
      </style>
      <span class="addon-switch-loader__spinner"></span>
      <span>Processing…</span>
    `;
    document.body.appendChild(loader);
  }

  hideLoader() {
    document.querySelector(".addon-switch-loader")?.remove();
  }

  connectedCallback() {
    this.addEventListener("mousedown", this.handlePreChange);
    this.addEventListener("touchstart", this.handlePreChange);
    this.addEventListener("change", this.handleChange);

    if (!this.isCartPage) {
      window.addEventListener("aftersell-upcart:public-events:cart-updated", this.onCartUpdated);
      window.addEventListener("aftersell-upcart:public-events:cart-loaded", this.onCartUpdated);
    }

    const checkedInput = this.querySelector("input:checked[data-role='Add']");
    this._currentVariantId = checkedInput ? String(checkedInput.value) : null;

    this.disableAddonQuantity();
  }

  disconnectedCallback() {
    this.removeEventListener("mousedown", this.handlePreChange);
    this.removeEventListener("touchstart", this.handlePreChange);
    this.removeEventListener("change", this.handleChange);
    window.removeEventListener("aftersell-upcart:public-events:cart-updated", this.onCartUpdated);
    window.removeEventListener("aftersell-upcart:public-events:cart-loaded", this.onCartUpdated);
    this.hideLoader();
  }

  handlePreChange(evt) {
    const input = evt.target.closest("input[type='radio']");
    if (!input) return;
    const checked = this.querySelector("input:checked");
    this._currentVariantId = (checked && checked.value !== "none") ? String(checked.value) : null;
  }

  onCartUpdated() {
    this.syncCheckedState();
    this.disableAddonQuantity();
  }

  syncCheckedState() {
    const cart = this.getCart();
    const addonVariantInCart = cart.items
      .map((item) => String(item.variant_id))
      .find((id) => this.allInputIds.includes(id));

    this.allInputs.forEach((input) => {
      if (input.dataset.role === "Remove") {
        input.checked = !addonVariantInCart;
      } else {
        input.checked = addonVariantInCart === String(input.value);
      }
    });

    this._currentVariantId = addonVariantInCart ?? null;
  }

  disableAddonQuantity() {
    document.querySelectorAll("input.quantity-selector__input").forEach((input) => {
      const variantId = String(input.dataset.lineKey || "").split(":")[0];
      if (!this.allInputIds.includes(variantId)) return;

      input.setAttribute("disabled", "disabled");
      input.style.opacity = "0.5";
      input.style.pointerEvents = "none";

      const quantitySelector = input.closest("quantity-selector");
      if (quantitySelector) {
        quantitySelector.querySelectorAll(".quantity-selector__button").forEach((btn) => {
          btn.removeAttribute("href");
          btn.style.pointerEvents = "none";
          btn.style.opacity = "0.5";
          btn.style.cursor = "not-allowed";
        });
      }

      const cartItemRow = input.closest("tr, .cart-item, .line-item");
      if (cartItemRow) {
        const removeLink = cartItemRow.querySelector("#remove-link");
        if (removeLink && this.dataset.disableRemove === "true") removeLink.remove();

        const productUrl = document.querySelector(`.cart_index[data-variant="${variantId}"] a`);
        if (productUrl) {
          productUrl.removeAttribute("href");
          productUrl.style.pointerEvents = "none";
          productUrl.style.opacity = "0.5";
          productUrl.style.cursor = "not-allowed";
        }
      }
    });
  }

  async handleChange(evt) {
    const input = evt.target;
    if (!input || input.tagName !== "INPUT") return;
    if (this._busy) return;
    this._busy = true;

    const selectedVariantId = String(input.value);
    const role = input.dataset.role;
    const sectionId = input.dataset.sectionId;
    const quantity = Number(input.dataset.quantity || "1");

    if (!role) { this._busy = false; return; }

    this.showLoader();

    try {
      if (role === "Add" && selectedVariantId && selectedVariantId !== "none") {
        await this.applySwitch(selectedVariantId, quantity, sectionId);
      }
      if (role === "Remove") {
        await this.applySwitch(null, 0, sectionId);
      }
    } finally {
      this.hideLoader();
      this._busy = false;
    }
  }

  async applySwitch(incomingVariantId, quantity, sectionId) {
    const previousVariantId = this._currentVariantId;

    // Nothing to add and nothing to remove
    if (!incomingVariantId && !previousVariantId) return;

    // Switching to the same variant — no-op
    if (incomingVariantId === previousVariantId) return;

    const updates = {};

    // Remove previous: look up the line key from UpCart cache for a precise remove
    if (previousVariantId) {
      const cart = this.getCart();
      const prevItem = cart.items.find((i) => String(i.variant_id) === previousVariantId);
      updates[prevItem ? prevItem.key : previousVariantId] = 0;
    }

    // Add incoming
    if (incomingVariantId) {
      updates[incomingVariantId] = quantity;
    }

    const body = { updates };
    if (this.isCartPage && sectionId) body.sections = [sectionId];

    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json();

    this._currentVariantId = incomingVariantId ?? null;

    if (this.isCartPage && data?.sections?.[sectionId]) {
      this.replaceHTML(data.sections[sectionId], sectionId);
    }
  }

  replaceHTML(source, sectionId) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = source;

    const newSection = tempDiv.querySelector(`#shopify-section-${sectionId}`);
    const currentSection = document.querySelector(`#shopify-section-${sectionId}`);
    if (!newSection || !currentSection) return;

    // Define the specific sub-selectors you want to update
    const selectorsToUpdate = ['table.order-summary', '.cart-footer'];

    selectorsToUpdate.forEach(selector => {
      const newTarget = newSection.querySelector(selector);
      const currentTarget = currentSection.querySelector(selector);

      // Only update if the sub-element exists in both the incoming AJAX HTML and the current page
      if (newTarget && currentTarget) {
        currentTarget.innerHTML = newTarget.innerHTML;
      }
    });

    this.disableAddonQuantity();
  }
}

if (!window.customElements.get("addon-switch")) {
  window.customElements.define("addon-switch", AddonSwitch);
}

// Insta Slider
document.addEventListener('DOMContentLoaded', (event) => {
  $('.instaSlider').slick({
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    autoplay: false,
    autoplaySpeed: 3000,
    prevArrow:
      "<img class='slick-prev' title='Prev Slide' aria-label='Prev Slide' alt='Prev Slide' src='https://cdn.shopify.com/s/files/1/0672/5766/3685/files/left-arrow.png?v=1734344508' width=20px>",
    nextArrow:
      "<img class='slick-next' title='Next Slide' aria-label='Next Slide' alt='Next Slide' src='https://cdn.shopify.com/s/files/1/0672/5766/3685/files/arrow-right.png?v=1734344508'  width=20px>",
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
    ],
  });


  // Title Class
    let pageTitle = document.querySelector('title').innerText.trim().toLowerCase().replace(/\s+/g, '-');

    let mainElement = document.querySelector('main#main');

    if (mainElement && pageTitle) {
      mainElement.classList.add(pageTitle);
    }

    let sections = mainElement ? mainElement.querySelectorAll('section') : [];

    sections.forEach((section, index) => {
      let sectionClass = `${pageTitle}-section-${index + 1}`;
      section.classList.add(sectionClass);
    });


  // Check if the page is a product page or collection page where price is leaked
  if (window.location.pathname.includes('/products/') || window.location.pathname.includes('/collections/')) {
    const pixelSetupScript = document.getElementById('web-pixels-manager-setup');
    if (pixelSetupScript) {
      // Remove the script tag entirely before it runs completely
      pixelSetupScript.remove();
      //console.log('Web Pixels Manager script removed to prevent price leakage.');
    }
  }


  // Delete Price from meta
  if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
    //console.log('Shopify Analytics Meta object found. Checking for price data...');

    if (window.ShopifyAnalytics.meta.product.variants) {
      window.ShopifyAnalytics.meta.product.variants.forEach((v) => {
        if (v.price !== undefined) {
          delete v.price;

          //console.log(`Price successfully removed from variant ID: ${v.id}.`);
        }
      });

      //console.log('Analytics price clearing script finished.');
    } else {
      //console.log('No product variants found in Shopify Analytics Meta.');
    }
    } else {
    //console.log('Shopify Analytics Meta object not found on this page.');
  }


// Lookbook settings

        const targetText = 'View our look book';
        const paragraphElements = document.querySelectorAll('p.h5');
        let lookbookLinkElement = null;
        paragraphElements.forEach((p) => {
          if (p.textContent.trim() === targetText) {
            const parentDiv = p.closest('.multi-column__item');
            if (parentDiv) {
              lookbookLinkElement = parentDiv.querySelector('a');
            }
          }
        });
        const actualLookbookURL = 'https://online.anyflip.com/rhdev/vkfs/mobile/index.html';
        const loginURL = '/account/login';
        const isLoggedIn = window.customerIsLoggedIn;
        const tags = window.customerTags || [];
        const hasWholesaleTag = tags.includes('Wholesale');
        if (lookbookLinkElement) {
          if (isLoggedIn && hasWholesaleTag) {
            lookbookLinkElement.href = actualLookbookURL;
          } else {
            lookbookLinkElement.href = loginURL;
            lookbookLinkElement.addEventListener('click', function (e) {
              if (lookbookLinkElement.href === loginURL) {
                e.preventDefault();
                window.location.href = loginURL;
              }
            });
          }
        }

});